import {Component, computed, output, input, inject, signal, effect, untracked} from '@angular/core';
import moment from 'moment';
import {AdaptateurEcranService} from '../../services/adaptateur-ecran.service';
import {ModeSelecteurPeriode} from '../../models/mode-selecteur-periode.enum';
import {DifferencePeriode} from '../../models/difference-periode.model';

@Component({
	selector: 'app-selecteur-periode',
	templateUrl: './selecteur-periode.component.html',
	styleUrl: './selecteur-periode.component.scss'
})
export class SelecteurPeriodeComponent {
	// Constantes
	readonly MODE_SELECTEUR_PERIODE = ModeSelecteurPeriode;

	// Injections
	private adaptateurEcranService = inject(AdaptateurEcranService);

	// Inputs
	boutonsDesactives = input<boolean>(false);
	mode = input<ModeSelecteurPeriode>(ModeSelecteurPeriode.SEMAINE);
	peutNaviguerDansPasse = input<boolean>(true);

	// Outputs
	changerPeriode = output<DifferencePeriode>();

	// Signaux
	differenceNombreJours = signal<number | null>(null);
	differenceNombreSemaines = signal(0);

	// Computed
	aujourdhui = computed(() => new Date());
	estAffichageMobile = computed(() => this.adaptateurEcranService.estAffichageMobile());
	journee = computed(() => {
		return moment().add(this.differenceNombreSemaines(), 'weeks')
			.add(this.differenceNombreJours(), 'days');
	});
	numeroSemaine = computed(() => moment().add(this.differenceNombreSemaines(), 'weeks').isoWeek());

	/**
	 * Indique si le bouton "période précédente" est désactivée en fonction des conditions suivantes :
	 * Les boutons sont désactivés.
	 * La navigation dans le passé n'est pas autorisée et période précédente est dans le passé.
	 */
	estPeriodePrecedenteDesactivee = computed(() => this.boutonsDesactives() ||
			(!this.peutNaviguerDansPasse() && this.differenceNombreSemaines() === 0 && (this.differenceNombreJours() === 0 || this.differenceNombreJours() === null))
	);

	constructor() {
		this.ecouterChangementsEcran();
	}

	/**
	 * Écoute les changements d'écran et adapte le composant en conséquence.
	 */
	ecouterChangementsEcran(): void {
		effect(() => {
			this.mode();
			untracked(() => this.retournerALaPeriodeActuelle());
		}, { allowSignalWrites: true });
	}

	/**
	 * Avance à la période suivante et émet l'output.
	 */
	passerPeriodeSuivante(): void {
		this.modifierPeriode(1);
	}

	/**
	 * Reviens à la période précédente et émet l'output.
	 */
	retournerPeriodePrecedente(): void {
		this.modifierPeriode(-1);
	}

	/**
	 * Incrémente ou décrémente la période selon la direction spécifiée.
	 *
	 * @param direction La direction de modification en semaines ou jours selon le mode (1 pour avancer, -1 pour reculer).
	 */
	private modifierPeriode(direction: 1 | -1): void {
		if (this.mode() === ModeSelecteurPeriode.JOUR) {
			const limite = 6 * direction;
			const estEnBordure = this.differenceNombreJours() === limite;
			const differenceJours = this.differenceNombreJours() ?? 0;
			this.differenceNombreJours.set(estEnBordure ? 0 : differenceJours + direction);

			if (estEnBordure) this.differenceNombreSemaines.update(v => v + direction);
		}
		else {
			this.differenceNombreSemaines.update(v => v + direction);
		}
		this.changerPeriode.emit(new DifferencePeriode(this.differenceNombreSemaines(), this.differenceNombreJours()));
	}

	/**
	 * Met à jour les différences de semaines et de jours en fonction de la date sélectionnée dans le calendrier et émet l'output.
	 *
	 * @param date la date sélectionnée.
	 */
	selectionnerDate(date: Date): void {
		if (this.mode() == ModeSelecteurPeriode.JOUR) {
			const diffJours = moment(date).isoWeekday() - moment().isoWeekday();
			this.differenceNombreJours.set(diffJours);
		}
		const maintenant = moment().startOf('isoWeek');
		const dateSelectionnee = moment(date).startOf('isoWeek');
		const difference = dateSelectionnee.diff(maintenant, 'weeks');
		this.differenceNombreSemaines.set(difference);
		this.changerPeriode.emit(new DifferencePeriode(this.differenceNombreSemaines(), this.differenceNombreJours()));
	}

	/**
	 * Reviens à la période actuelle (semaine ou journée courante) et émet les nouvelles valeurs.
	 * N'émet que si au moins une des valeurs de différence est différente de sa valeur initiale.
	 */
	retournerALaPeriodeActuelle(): void {
		const valeurInitialeJours = this.mode() === ModeSelecteurPeriode.JOUR ? 0 : null;
		const dejaSurLaPeriodeActuelle = this.differenceNombreSemaines() === 0 && this.differenceNombreJours() === valeurInitialeJours;

		if (!dejaSurLaPeriodeActuelle) {
			this.differenceNombreSemaines.set(0);
			this.differenceNombreJours.set(valeurInitialeJours);
			this.changerPeriode.emit(new DifferencePeriode(this.differenceNombreSemaines(), this.differenceNombreJours()));
		}
	}
}
